import React, { useEffect, useMemo } from "react";
import { useI18n } from "@bookingcom/lingojs-react";
import { Divider } from "@bookingcom/bui-react";
import {
  BrandedFareClientFeature,
  UIBrandedFareFeature,
  UIBrandedFareInfo,
  UIIncludedProductBySegmentCompressed
} from "@flights/types";
import { getFeaturesToRender } from "../../utils/features";
import FareFeature from "../FareFeature";
import styles from "./FareFeatures.module.css";
import Frame from "../../../components/elements/Frame";
import FareFeatureHeading from "./FareFeatureHeading";
import useUserAgent from "../../../hooks/useUserAgent";
import { CHANGE_FEATURES, overrideSeatSelectedAtAirlineCopy, PRIORITY_FEATURE_NAMES } from "@flights/branded-fares";
import useRouteName from "../../../hooks/useRouteName";
import flights_apex_web_bf_sellable_bags from "../../../utils/experiments/apex/flights_apex_web_bf_sellable_bags";
import flights_apex_web_show_sellable_and_excluded_seats from "../../../utils/experiments/apex/flights_apex_web_show_sellable_and_excluded_seats";
import { useSelector } from "react-redux";
import { getIsMultiTraveller } from "../../../store/flightDetails/selectors";
import useIsPostBookingRoute from "../../../hooks/useIsPostBookingRoute";
import flights_apex_web_show_checked_baggage_exclusions from "../../../utils/experiments/apex/flights_apex_web_show_checked_baggage_exclusions";
import { useHasInfantTraveller } from "../../../hooks/useHasInfantTraveller";
import { DollarIcon } from "@bookingcom/bui-assets-react/streamline";
import { getIsSeatMapAvailable } from "../../../store/selectors/checkoutSteps";
import { FastTrackIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import flights_apex_web_bf_cta_visibility_v2 from "../../../utils/experiments/apex/flights_apex_web_bf_cta_visibility_v2";
import { t } from "@bookingcom/lingojs-core";

const isClient = process.env.BUILD_TARGET === "client";

type Props = {
  brandedFareInfo: UIBrandedFareInfo;
  sellableFeaturesRequiredByAirline?: boolean;
  excludedFeaturesRequiredByAirline?: boolean;
  isPreCheckAndPay?: boolean;
  showDivider?: boolean;
  showHeader?: boolean;
  showDescriptions?: boolean;
  showSubtitles?: boolean;
  compactHeight?: boolean;
  showExcludedFeatures?: boolean;
  areShown?: boolean;
  showChangeDisclaimerStar?: boolean;
  uniqueUpsellFeatures?: UIBrandedFareFeature[];
  travellers?: number;
  hasSellableBag?: boolean;
  showUpsellFeatureUpgradeIcon?: boolean;
  includedProducts?: UIIncludedProductBySegmentCompressed;
};

const FareFeatures = ({
  brandedFareInfo,
  sellableFeaturesRequiredByAirline,
  excludedFeaturesRequiredByAirline = false,
  showHeader,
  showDivider,
  isPreCheckAndPay,
  showDescriptions,
  showSubtitles,
  compactHeight = false,
  showExcludedFeatures = true,
  areShown,
  showChangeDisclaimerStar,
  uniqueUpsellFeatures = [],
  travellers,
  hasSellableBag,
  showUpsellFeatureUpgradeIcon,
  includedProducts
}: Props) => {
  const i18n = useI18n();
  const { isMobile } = useUserAgent();
  const routeName = useRouteName();
  const isMultiTraveller = useSelector(getIsMultiTraveller);
  const isSeatMapAvailable = useSelector(getIsSeatMapAvailable);

  if (!brandedFareInfo) throw new Error("FareSwitcher: missing branded fare info");

  const showSellableBags = isClient && !!flights_apex_web_bf_sellable_bags.trackWithDefaultStage();
  const showSellableOrExcludedSeats =
    isClient && !!flights_apex_web_show_sellable_and_excluded_seats.trackWithDefaultStage();

  useEffect(() => {
    if (hasSellableBag && isClient)
      flights_apex_web_bf_sellable_bags.trackStages(isMobile, travellers, sellableFeaturesRequiredByAirline);
  }, [hasSellableBag, isMobile, sellableFeaturesRequiredByAirline, travellers]);

  const featuresToRender = useMemo(() => {
    return getFeaturesToRender(brandedFareInfo, {
      includeSellableFeatures: sellableFeaturesRequiredByAirline,
      includeExcludedFeatures: excludedFeaturesRequiredByAirline,
      includeSellableBaggageFeatures: showSellableBags,
      includeSellableOrExcludedSeats: showSellableOrExcludedSeats,
      isPreCheckAndPay
    });
  }, [
    brandedFareInfo,
    sellableFeaturesRequiredByAirline,
    isPreCheckAndPay,
    excludedFeaturesRequiredByAirline,
    showSellableBags,
    showSellableOrExcludedSeats
  ]);

  const allIncludedFeatures = featuresToRender.includedFeatures;
  const sellableCheckedBaggageFeatures = allIncludedFeatures.filter(
    (f) => f.featureName === "CHECK_BAGGAGE" && f.availability === "SELLABLE" && showSellableBags
  );
  const includedAndSellableFeatures = allIncludedFeatures.filter(
    (f) => !(f.featureName === "CHECK_BAGGAGE" && f.availability === "SELLABLE" && showSellableBags)
  );
  const hasIncludedOrSellableFeatures = includedAndSellableFeatures.length > 0;
  const excludedFeatures = showExcludedFeatures ? featuresToRender.nonIncludedFeatures : [];
  const hasExcludedFeatures = excludedFeatures.length > 0;

  /* start - flights_apex_web_show_checked_baggage_exclusions */
  const isPostBookingRoute = useIsPostBookingRoute();
  const isSearchResultsPage = routeName === "searchresults";
  const hasInfantTraveller = useHasInfantTraveller();
  const hasCheckedBaggageExclusion = brandedFareInfo.nonIncludedFeatures?.some(
    (feature) => feature.featureName === "CHECK_BAGGAGE"
  );
  if (areShown !== false && hasCheckedBaggageExclusion && !isPostBookingRoute && !isSearchResultsPage) {
    const isCheckAndPay = routeName === "checkout-payment";
    flights_apex_web_show_checked_baggage_exclusions.trackStages(
      isMobile,
      isMultiTraveller,
      hasInfantTraveller,
      isCheckAndPay,
      excludedFeaturesRequiredByAirline
    );
  }
  /* end - flights_apex_web_show_checked_baggage_exclusions */

  const checkedInLuggageContent = useMemo(() => {
    if (!includedProducts?.segments?.[0]) return null;
    return includedProducts.segments[0].find((item) => item.luggageType === "CHECKED_IN")?.content;
  }, [includedProducts]);

  if (!hasIncludedOrSellableFeatures && !hasExcludedFeatures) return null;

  let mainFeatures = includedAndSellableFeatures;

  if (!isSeatMapAvailable && !isPreCheckAndPay && showSellableOrExcludedSeats) {
    mainFeatures = mainFeatures.map((feature) => overrideSeatSelectedAtAirlineCopy(feature, i18n));
  }

  /* start - flights_apex_web_bf_cta_visibility_v2 */
  const getPriorityFeaturesLabel = (priorityFeatures: BrandedFareClientFeature[]) => {
    if (priorityFeatures.length === 2)
      return i18n.trans(
        t("flights_branded_fares_prio_services_2", {
          variables: {
            priority_service_1: priorityFeatures[0].label ?? "",
            priority_service_2: priorityFeatures[1].label ?? ""
          }
        })
      );
    if (priorityFeatures.length > 2)
      return i18n.trans(
        t("flights_branded_fares_prio_services_more", {
          num_exception: priorityFeatures.length - 1,
          variables: {
            priority_service_1: priorityFeatures[0].label ?? "",
            num_priority_services: priorityFeatures.length - 1
          }
        })
      );

    return "";
  };

  const priorityFeatures = mainFeatures.filter((feature) => PRIORITY_FEATURE_NAMES.includes(feature.featureName));
  const isBrandedFarePage = routeName === "checkout-fare";
  const canGroupPriorityFeatures = isBrandedFarePage && priorityFeatures.length > 1;
  const shouldGroupPriorityFeatures =
    canGroupPriorityFeatures && flights_apex_web_bf_cta_visibility_v2.trackWithDefaultStage();
  if (canGroupPriorityFeatures) {
    flights_apex_web_bf_cta_visibility_v2.trackStages(isMobile, priorityFeatures.length);
  }
  if (shouldGroupPriorityFeatures) {
    // remove priority features from the main features
    mainFeatures = mainFeatures.filter((feature) => !PRIORITY_FEATURE_NAMES.includes(feature.featureName));
  }
  /* end - flights_apex_web_bf_cta_visibility_v2 */

  const getFeatureContent = (feature: typeof mainFeatures[number]) => {
    if (feature.featureName === "CHECK_BAGGAGE" && checkedInLuggageContent?.featureLabel) {
      return checkedInLuggageContent.featureLabel;
    }

    const disclaimerStar = showChangeDisclaimerStar && CHANGE_FEATURES.includes(feature.featureName) ? "*" : "";

    return (feature.label ?? "") + disclaimerStar;
  };

  const getFeatureSubtitle = (feature: typeof mainFeatures[number]) => {
    if (!showSubtitles) return undefined;

    if (feature.featureName === "CHECK_BAGGAGE" && checkedInLuggageContent?.subtitle) {
      return checkedInLuggageContent.subtitle;
    }

    return feature.subtitle;
  };

  return (
    <>
      {showHeader && hasIncludedOrSellableFeatures && (
        <FareFeatureHeading>{i18n.trans(t("flights_bf_included_total"))}</FareFeatureHeading>
      )}

      {mainFeatures.map((feature) => (
        <FareFeature
          key={feature.featureName}
          id={feature.featureName}
          IconSVG={feature.icon}
          content={getFeatureContent(feature)}
          description={showDescriptions ? feature.description : undefined}
          subtitle={getFeatureSubtitle(feature)}
          availability={feature.availability}
          compactHeight={compactHeight}
          isUniqueUpsellFeature={uniqueUpsellFeatures.some(
            (upsellFeature) => upsellFeature.featureName === feature.featureName
          )}
          showUpsellFeatureUpgradeIcon={showUpsellFeatureUpgradeIcon}
        />
      ))}

      {shouldGroupPriorityFeatures ? (
        <FareFeature
          id="PRIORITY_FEATURES"
          IconSVG={FastTrackIcon}
          content={getPriorityFeaturesLabel(priorityFeatures)}
          availability="INCLUDED"
          // only need a popover for more than 2 features, otherwise they will be included in the label
          popoverFeatures={priorityFeatures.length > 2 ? priorityFeatures : []}
        />
      ) : null}

      {showDivider && hasExcludedFeatures && hasIncludedOrSellableFeatures && (
        <Frame mb={4}>
          <Divider className={styles.divider} attributes={{ "data-testid": "fareFeatureDivider" }} />
        </Frame>
      )}

      {sellableCheckedBaggageFeatures.map((feature) => (
        <FareFeature
          key={feature.featureName}
          id={feature.featureName}
          IconSVG={DollarIcon}
          content={feature.label}
          availability={feature.availability}
          compactHeight={compactHeight}
          isUniqueUpsellFeature={uniqueUpsellFeatures.some(
            (upsellFeature) => upsellFeature.featureName === feature.featureName
          )}
          showUpsellFeatureUpgradeIcon={showUpsellFeatureUpgradeIcon}
        />
      ))}

      {excludedFeatures.map((feature) => (
        <FareFeature
          key={feature.featureName}
          content={feature.label}
          availability={feature.availability}
          IconSVG={feature.icon}
          compactHeight={compactHeight}
        />
      ))}
    </>
  );
};

export default FareFeatures;
